/**
 * Upcoming Events Widget
 *
 * Displays the next upcoming events across all calendars.
 * Configurable count and click-to-navigate functionality.
 *
 * @module shared/components/widgets/UpcomingEventsWidget
 */

import { format, isToday, isTomorrow, isPast, parseISO } from 'date-fns';
import { bg } from 'date-fns/locale';
import { Calendar, Clock, MapPin, CalendarDays } from 'lucide-react';
import { useMemo } from 'react';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@/shared/components/ui/card';
import { TruncatedText } from '@/shared/components/ui/truncated-text';
import { RouteKey } from '@/shared/constants/routes';
import { useAppNavigation } from '@/shared/hooks/useAppNavigation';
import { toLocalDateKey } from '@/shared/lib/dateKeys';
import { cn } from '@/shared/lib/utils';
import { BadgeVariant } from '@/shared/types/badge.types';
import { ButtonVariant, ButtonSize } from '@/shared/types/button.types';

// ============================================
// Types
// ============================================

type EventCount = 3 | 5 | 10;

/** Minimal event shape consumed by the upcoming events list. */
interface UpcomingEvent {
  readonly id: string;
  readonly title: string;
  readonly startDate: string;
  readonly endDate?: string | null;
  readonly isAllDay: boolean;
  readonly emoji?: string | null;
  readonly location?: string | null;
  readonly labelId?: string | null;
}

/** Minimal label shape used to colour events. */
interface UpcomingEventLabel {
  readonly id: string;
  readonly color: string;
}

interface UpcomingEventsWidgetProps {
  /** Events to display (already loaded from the store by a container) */
  readonly events?: readonly UpcomingEvent[];
  /** Labels used to colour events */
  readonly labels?: readonly UpcomingEventLabel[];
  /** Number of events to display */
  readonly count?: EventCount;
  /** Additional CSS classes */
  readonly className?: string;
  /** Show header */
  readonly showHeader?: boolean;
  /** Show empty state */
  readonly showEmptyState?: boolean;
}

// ============================================
// Helpers
// ============================================

/**
 * Format event date/time for display
 */
function formatEventDateTime(event: UpcomingEvent): string {
  const startDate = parseISO(event.startDate);

  // Relative date labels
  if (isToday(startDate)) {
    if (event.isAllDay) return 'Днес';
    return `Днес, ${format(startDate, 'HH:mm', { locale: bg })}`;
  }

  if (isTomorrow(startDate)) {
    if (event.isAllDay) return 'Утре';
    return `Утре, ${format(startDate, 'HH:mm', { locale: bg })}`;
  }

  // Full date
  if (event.isAllDay) {
    return format(startDate, 'd MMM', { locale: bg });
  }

  return format(startDate, 'd MMM, HH:mm', { locale: bg });
}

/**
 * Get relative date badge variant
 */
function getDateBadgeVariant(date: Date): BadgeVariant {
  if (isPast(date) && !isToday(date)) return BadgeVariant.Destructive;
  if (isToday(date)) return BadgeVariant.Default;
  return BadgeVariant.Secondary;
}

// ============================================
// Event Item Component
// ============================================

interface EventItemProps {
  readonly event: UpcomingEvent;
  readonly labelColor: string | undefined;
  readonly onNavigate: () => void;
}

function EventItem({ event, labelColor, onNavigate }: EventItemProps) {
  const startDate = parseISO(event.startDate);
  const dateVariant = getDateBadgeVariant(startDate);

  return (
    <button
      type="button"
      onClick={onNavigate}
      className={cn(
        'flex items-start gap-3 p-2 -mx-2 rounded-lg w-full text-left',
        'hover:bg-accent/50 transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1'
      )}
    >
      {/* Color indicator */}
      <div
        className="mt-0.5 h-10 w-1 flex-shrink-0 rounded-full"
        style={{
          backgroundColor:
            labelColor !== undefined ? labelColor : 'hsl(var(--primary))',
        }}
        aria-hidden="true"
      />

      {/* Event details */}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1.5">
            {event.emoji && (
              <span className="text-sm" aria-hidden="true">
                {event.emoji}
              </span>
            )}
            <TruncatedText className="text-sm font-medium">
              {event.title}
            </TruncatedText>
          </div>
          <Badge variant={dateVariant} className="flex-shrink-0 text-xs">
            {formatEventDateTime(event)}
          </Badge>
        </div>

        <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
          {!event.isAllDay && (
            <span className="flex items-center gap-1">
              <Clock className="size-3" />
              {format(startDate, 'HH:mm', { locale: bg })}
              {event.endDate && (
                <>
                  {' - '}
                  {format(parseISO(event.endDate), 'HH:mm', { locale: bg })}
                </>
              )}
            </span>
          )}
          {event.location && (
            <span className="flex min-w-0 items-center gap-1">
              <MapPin className="size-3 flex-shrink-0" />
              <TruncatedText className="text-xs text-muted-foreground">
                {event.location}
              </TruncatedText>
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

// ============================================
// Main Widget Component
// ============================================

export function UpcomingEventsWidget({
  events = [],
  labels = [],
  count = 5,
  className,
  showHeader = true,
  showEmptyState = true,
}: UpcomingEventsWidgetProps) {
  const { goToCalendar, navigateTo } = useAppNavigation();

  // Create label color map
  const labelColors = useMemo(() => {
    const map = new Map<string, string>();
    for (const label of labels) {
      map.set(label.id, label.color);
    }
    return map;
  }, [labels]);

  // Get upcoming events (sorted by start date, excluding past all-day events)
  const upcomingEvents = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );

    return [...events]
      .filter((event) => {
        const startDate = parseISO(event.startDate);
        // Include if event hasn't ended yet
        if (event.isAllDay) {
          return startDate >= todayStart;
        }
        return (
          startDate >= now || (event.endDate && parseISO(event.endDate) >= now)
        );
      })
      .sort(
        (a, b) =>
          parseISO(a.startDate).getTime() - parseISO(b.startDate).getTime()
      )
      .slice(0, count);
  }, [events, count]);

  // Navigate to event
  const handleEventClick = (event: UpcomingEvent) => {
    const dateKey = toLocalDateKey(parseISO(event.startDate));
    goToCalendar({ date: dateKey, eventId: event.id });
  };

  // Navigate to the All Events list page
  const handleViewAll = () => {
    navigateTo({ route: RouteKey.ALL_EVENTS });
  };

  // Navigate to the calendar, where events are created
  const handleAddEvent = () => {
    goToCalendar();
  };

  return (
    <Card className={cn('overflow-hidden', className)}>
      {showHeader && (
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-1.5 text-sm">
              <CalendarDays className="size-4" />
              Предстоящи събития
            </CardTitle>
            <Button
              variant={ButtonVariant.Ghost}
              size={ButtonSize.Small}
              className="h-7 px-2 text-xs"
              onClick={handleViewAll}
            >
              Виж всички
            </Button>
          </div>
        </CardHeader>
      )}

      <CardContent className={cn('p-4', showHeader && 'pt-0')}>
        {upcomingEvents.length > 0 ? (
          <div className="space-y-1">
            {upcomingEvents.map((event) => (
              <EventItem
                key={event.id}
                event={event}
                labelColor={
                  event.labelId ? labelColors.get(event.labelId) : undefined
                }
                onNavigate={() => handleEventClick(event)}
              />
            ))}
          </div>
        ) : showEmptyState ? (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <Calendar className="mb-2 size-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              Няма предстоящи събития
            </p>
            <Button
              variant={ButtonVariant.Link}
              size={ButtonSize.Small}
              className="mt-1 text-xs"
              onClick={handleAddEvent}
            >
              Добави събитие
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

// Default export for easier imports
export default UpcomingEventsWidget;
